/**
 * feat-242 — the curriculum Map. A full-width, READ-ONLY canvas of everything a
 * tenant has authored: Tracks (active and Hidden) in dependency tiers, Lessons
 * drawn inside the Track(s) that contain them, an "In no {Track}" region, four
 * named structural hazards, and SVG connectors between Track nodes.
 *
 * ─── NOTHING HERE WRITES. NOTHING. ────────────────────────────────────────
 * Clicking a Track node switches to Editor with it selected; clicking a Lesson
 * chip jumps to the Lessons tab. That is the whole interaction surface. No drag
 * target, no inline picker, no "file into…" affordance however obvious it looks
 * — a membership write must not fire from a surface that cannot show the
 * ordering it disturbs, and two screens must not both be able to fire the same
 * last-write-wins `PUT …/lessons`. AC18 asserts this module set's imports BY
 * COUNT AS A SET, so a fourth import added later goes red rather than sliding
 * in. Slice 4 shipped a correctly-gated record FAB while the Home tab kept a
 * live two-tap path to audio capture; only `security-reviewer` caught it. A
 * population defect is not closed at one call site.
 *
 * ─── D11, THE BOUNDARY THIS VIEW IS BUILT AGAINST ─────────────────────────
 *   > The Map may make statements about the CURRICULUM.
 *   > It may never make a statement about a LEARNER.
 * Every hazard below holds for every learner simultaneously — that is what makes
 * it a curriculum fact rather than a client-side unlock evaluator, which the
 * program's Non-goals forbid outright. All copy lives in `mapCopy.ts`; AC13's
 * census sweeps the rendered output against the pattern list exported there.
 *
 * ─── TWO VISUAL RULINGS THAT LOOK LIKE BUGS AND ARE NOT (Q11) ─────────────
 * 1. A Track node's border STYLE and COLOUR are orthogonal. *Dashed* = the Track
 *    is Hidden. *Amber* = the Track carries a track-level mark (H2/H3/H4). A
 *    Hidden Track with no mark is dashed and NEUTRAL. Letting amber also mean
 *    "Hidden" would make the one colour on this canvas mean two things with no
 *    way to tell which.
 * 2. A Hidden Track is dashed, NEVER dimmed. `TrackList` uses `opacity-60` on an
 *    inactive row and that is right *there* — a list row has nothing inside it.
 *    A Map node does, and dimming the box dims the H1 marks inside it, which is
 *    exactly where spec §5 puts the hidden-only unreachable Lessons. Do not add
 *    an `opacity-60` here "to match TrackList".
 *
 * ─── AND ONE COUNT THAT IS DELIBERATELY ABSENT (Q4) ───────────────────────
 * There is NO roll-up hazard total anywhere on this canvas. The library footer
 * counts `unassigned_lesson_ids` — no Track at all — while H1's population also
 * contains Lessons filed only into Hidden Tracks. The two numbers differ
 * legitimately. A "3 problems" chip at the top is precisely the thing that would
 * make them contradict each other in one glance.
 */

import React, { useMemo, useState } from 'react';
import type { LearnTrack, QuizUnit } from '../../../../../services/quizContentAdminService';
import {
  ATTENTION_BORDER,
  ATTENTION_INK,
  ATTENTION_MARK,
  ATTENTION_OUTLINE,
  ATTENTION_SURFACE,
} from '../attentionClasses';
import TrackSwatch from '../TrackSwatch';
import { buildCurriculumMap } from '../trackGraph';
import type { CurriculumMap } from '../trackGraph';
import MapEdgeLayer from './MapEdgeLayer';
import { HAZARD_GLYPH, MAP_COPY, MAP_LABELS } from './mapCopy';

/**
 * THE STATUS PILL CLASSES — one named reference, deliberately, and the reason is
 * worth reading before you "fix" them.
 *
 * These are what `feat-240-learn-status-pill-contrast-aa` LANDS (TOKENS.md §1e:
 * `dash-success-light #EAF4E5` / `dash-success-on #3D7A2B`, 4.623:1). They are
 * NOT what is on this branch today — `success.on` has no Tailwind binding here
 * yet, because that binding is feat-240's to land and this feature is scoped out
 * of the pill colours entirely.
 *
 * So until feat-240 merges, `text-success-on` resolves to nothing and the pill
 * text falls back to inherited ink on the correct fill: the wrong hue, still
 * legible, still AA, and self-healing the moment feat-240 lands. The alternative
 * — writing today's `bg-success/15 text-success` — ships a FOURTH surface
 * rendering this semantic in the old 2.538:1 green, which outlives the window
 * and which nobody would be looking for.
 *
 * Do not copy class strings out of a pane; take them from TOKENS.md §1e. And
 * keep them here, in one pair: if the ruling moves, it is one edit.
 * Recorded in full in `reviews/FRONTEND-NOTES.md` N1.
 */
export const MAP_STATUS_PILL_ON = 'bg-success-light text-success-on';
export const MAP_STATUS_PILL_HIDDEN = 'bg-gray-100 text-inkMuted';

const PILL_SHAPE = 'text-[10px] font-bold rounded-full px-1.5 py-0.5 shrink-0';

export interface CurriculumMapViewProps {
  tracks: LearnTrack[];
  /** The tenant's whole Lesson library (level 1). */
  units: QuizUnit[];
  /** Server-computed set difference over the whole tenant. NEVER re-derived from
   *  `tracks` — that is only correct while the client holds every Track, and the
   *  type's own docstring says so. */
  unassignedLessonIds: string[];
  /** Switch to the Editor view with this Track selected. */
  onOpenTrack: (trackId: string) => void;
  /** Jump to the Lessons tab with this Lesson selected — the reverse of the jump
   *  `LessonTracksStrip`'s chips already perform. Optional: where the host has
   *  not wired it, the chips render as plain text rather than as buttons that
   *  do nothing. */
  onOpenLesson?: (lessonId: string) => void;
}

const StatusPill: React.FC<{ active: boolean; testId?: string; small?: boolean }> = ({
  active,
  testId,
  small,
}) => (
  <span
    data-testid={testId}
    className={`${active ? MAP_STATUS_PILL_ON : MAP_STATUS_PILL_HIDDEN} ${PILL_SHAPE} ${
      small ? 'text-[9.5px] py-0' : 'min-w-[48px] text-center'
    }`}
  >
    {active ? MAP_LABELS.on : MAP_LABELS.hidden}
  </span>
);

/** A hazard sentence on a Track header. Glyph + copy, never hue alone.
 *
 *  That is not a stylistic preference — it is **rule 2 of the standing ruling**
 *  on the `attention-*` / `dash-gold-on` collision. Preston ruled it 2026-08-16:
 *  **ship both families as-is** (`TOKENS.md` §1o, "✅ RULED"; commit `bafedd2`).
 *  The three rules there are the standing state, NOT an interim measure, and
 *  this component satisfies rule 2 by pairing every mark with a glyph and a
 *  sentence so the two golds are never told apart by hue alone. */
const HazardMark: React.FC<{ glyph: string; testId: string; children: React.ReactNode }> = ({
  glyph,
  testId,
  children,
}) => (
  <p
    data-testid={testId}
    className={`mx-1.5 mt-1.5 rounded-lg ${ATTENTION_MARK} text-[11px] px-2 py-1.5 flex gap-1.5`}
  >
    <span aria-hidden className="font-bold shrink-0">
      {glyph}
    </span>
    <span>{children}</span>
  </p>
);

export const CurriculumMapView: React.FC<CurriculumMapViewProps> = ({
  tracks,
  units,
  unassignedLessonIds,
  onOpenTrack,
  onOpenLesson,
}) => {
  /** A CALLBACK REF, not `useRef` — see `MapEdgeLayer`'s `host` prop. A parent's
   *  ref is populated after its children's layout effects run, so the layer
   *  measured against `null` and drew nothing, silently. Holding the element in
   *  state makes its arrival a render the layer can react to. */
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  const map: CurriculumMap = useMemo(() => buildCurriculumMap(tracks, units), [tracks, units]);

  const unitsById = useMemo(() => {
    const m: Record<string, QuizUnit> = {};
    for (const u of units) m[u.id] = u;
    return m;
  }, [units]);

  const tracksById = useMemo(() => {
    const m: Record<string, LearnTrack> = {};
    for (const t of tracks) m[t.id] = t;
    return m;
  }, [tracks]);

  const trackCountByLesson = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const t of tracks) for (const id of t.lesson_ids) counts[id] = (counts[id] ?? 0) + 1;
    return counts;
  }, [tracks]);

  const titleOf = (id: string) => tracksById[id]?.title || '(untitled)';
  const joinTitles = (ids: string[]) => ids.map(titleOf).join(' and ');

  const activeCount = tracks.filter((t) => t.active).length;
  const unfiled = units.filter((u) => unassignedLessonIds.includes(u.id));

  const hazardFree =
    map.unreachableLessonIds.size === 0 &&
    Object.keys(map.deadEndByTrack).length === 0 &&
    Object.keys(map.ignoredByTrack).length === 0 &&
    map.loopGroups.length === 0;

  // ---------------------------------------------------------------- empty ---
  const nothingAuthored =
    tracks.length === 0 || (tracks.length === 1 && tracks[0].lesson_ids.length === 0);
  if (nothingAuthored && unfiled.length === 0) {
    return (
      <div
        data-testid="curriculum-map"
        className="flex-1 min-h-0 overflow-y-auto grid place-items-center p-8 text-center"
        style={{ minHeight: 480 }}
      >
        <div>
          <div className="text-sm font-bold">{MAP_COPY.emptyHeadline(tracks.length)}</div>
          <div className="text-xs text-inkMuted mt-1 max-w-[36ch] mx-auto">
            {MAP_COPY.emptyBody()}
          </div>
        </div>
      </div>
    );
  }

  // ----------------------------------------------------------- track node ---
  const renderTrackNode = (trackId: string) => {
    const t = tracksById[trackId];
    if (!t) return null;

    const deadEnd = map.deadEndByTrack[trackId];
    const ignored = map.ignoredByTrack[trackId];
    const loop = map.loopGroupByTrack[trackId];
    const isEmpty = map.emptyTrackIds.has(trackId);
    // Q11(1): STYLE carries Hidden, COLOUR carries "has a track-level mark".
    // The two are independent and must stay that way.
    const hasTrackMark = !!deadEnd || !!ignored || !!loop;
    const borderStyle = t.active ? 'border' : 'border border-dashed';
    const borderColour = hasTrackMark ? ATTENTION_BORDER : 'border-softBorder';

    return (
      <article
        key={trackId}
        data-node={trackId}
        data-testid={`map-track-node-${trackId}`}
        // Q11(2): no `opacity-60` on a Hidden node — it would dim the H1 marks
        // inside, which is exactly where the hidden-only unreachable Lessons are.
        className={`rounded-2xl bg-white shadow-sm ${borderStyle} ${borderColour}`}
      >
        <button
          type="button"
          data-testid={`map-track-${trackId}`}
          onClick={() => onOpenTrack(trackId)}
          title={MAP_COPY.trackNodeTitle(t.title || '(untitled)')}
          className={`w-full flex items-center gap-1.5 px-2.5 py-2 border-b border-softBorder text-left hover:bg-cream rounded-t-2xl ${
            t.active ? '' : 'border-dashed'
          }`}
        >
          <TrackSwatch
            title={t.title}
            imageKey={t.image_s3_key}
            className="w-6 h-6 rounded-md border border-softBorder"
            monogramClassName="text-[9px]"
            testId={`map-track-swatch-${trackId}`}
          />
          <span className="flex-1 truncate font-bold text-[13px]">{t.title || '(untitled)'}</span>
          {t.is_default && (
            <span className={`bg-preview-light text-preview ${PILL_SHAPE}`}>Default</span>
          )}
          <StatusPill active={t.active} testId={`map-track-status-${trackId}`} />
        </button>

        {/* H2 — a dead end behind it. The chain is carried so the sentence can
            name both hops; a one-hop implementation renders the wrong one. */}
        {deadEnd && (
          <HazardMark glyph={HAZARD_GLYPH.h2} testId={`map-h2-${trackId}`}>
            {deadEnd.path.length === 1
              ? MAP_COPY.deadEndDirect(titleOf(deadEnd.path[0]))
              : MAP_COPY.deadEndTransitive(
                  titleOf(deadEnd.path[0]),
                  titleOf(deadEnd.path[deadEnd.path.length - 1]),
                )}
          </HazardMark>
        )}

        {/* H3 — a requirement nothing acts on. */}
        {ignored && (
          <HazardMark glyph={HAZARD_GLYPH.h3} testId={`map-h3-${trackId}`}>
            {ignored.reason === 'open_mode'
              ? MAP_COPY.ignoredOpenMode(joinTitles(ignored.ignoredTrackIds))
              : MAP_COPY.ignoredHiddenRequired(
                  joinTitles(ignored.ignoredTrackIds),
                  ignored.gatingTrackIds.length > 0 ? joinTitles(ignored.gatingTrackIds) : null,
                )}
          </HazardMark>
        )}

        {/* H4 — on EVERY member's header, inside the shared ring below. */}
        {loop && (
          <HazardMark glyph={HAZARD_GLYPH.h4} testId={`map-h4-${trackId}`}>
            {MAP_COPY.loopGroupBody(loop.length)}
          </HazardMark>
        )}

        {isEmpty ? (
          /* An empty Track's own body. WHETHER THIS IS A HAZARD DEPENDS ENTIRELY
             ON WHETHER ANYTHING IS BEHIND IT.

             H2 is "a dead end behind it". A Track with no On Lessons that
             SOMETHING REQUIRES is the cause of a real dead end, and it takes the
             attention family — cause and effect on screen together.

             A Track with no On Lessons that NOTHING requires is just a Track the
             author has not filled in yet, and it takes the NEUTRAL treatment. It
             was taking the attention family unconditionally, which meant a
             brand-new tenant with two empty Tracks got two marks meaning "you
             authored this, it reaches nobody, and you can fix it" for having
             authored nothing yet. That is the paint-a-legitimate-state-as-an-
             error failure this slice exists to avoid, and the guard below is
             `requiredByCount`, not `isEmpty`, for exactly that reason.

             The old code had this guard — but on the SECOND SENTENCE only, not
             on the box. Gating the explanation while leaving the alarm is the
             worst of both. */
          (() => {
            const requiredBy = map.requiredByCount[trackId] ?? 0;
            const isDeadEndCause = requiredBy > 0;
            return (
              <div
                data-testid={`map-empty-track-${trackId}`}
                data-empty-kind={isDeadEndCause ? 'hazard' : 'neutral'}
                className={`m-1.5 rounded-xl border border-dashed px-2.5 py-3 text-center ${
                  isDeadEndCause ? `${ATTENTION_BORDER} ${ATTENTION_SURFACE}` : 'border-softBorder'
                }`}
              >
                {isDeadEndCause ? (
                  <>
                    <p className={`text-[11.5px] font-bold ${ATTENTION_INK}`}>
                      {MAP_COPY.emptyTrackHeadline()}
                    </p>
                    <p className={`text-[11px] ${ATTENTION_INK} mt-0.5`}>
                      {MAP_COPY.emptyTrackRequiredBy(requiredBy)}
                    </p>
                    {t.lesson_ids.length > 0 && (
                      <p className={`text-[11px] ${ATTENTION_INK} mt-0.5`}>
                        {MAP_COPY.emptyTrackAllHidden(t.lesson_ids.length)}
                      </p>
                    )}
                  </>
                ) : (
                  /* One line, factual, nothing reported. The all-Hidden case
                     states the count rather than "nothing yet", because there IS
                     something — it is just not On. */
                  <p className="text-[11.5px] text-inkMuted">
                    {t.lesson_ids.length === 0
                      ? MAP_COPY.emptyTrackNothingYet()
                      : MAP_COPY.emptyTrackAllHidden(t.lesson_ids.length)}
                  </p>
                )}
              </div>
            );
          })()
        ) : (
          <ol className="p-1.5 space-y-px">
            {t.lesson_ids.map((lessonId, i) => {
              const unit = unitsById[lessonId];
              if (!unit) return null;
              const unreachable = map.unreachableLessonIds.has(lessonId);
              const shared = trackCountByLesson[lessonId] ?? 0;
              return (
                <li key={`${trackId}-${lessonId}`}>
                  <button
                    type="button"
                    data-testid={`map-lesson-${trackId}-${lessonId}`}
                    onClick={() => onOpenLesson?.(lessonId)}
                    title={MAP_COPY.lessonChipTitle(unit.title || '(untitled)')}
                    className={`w-full text-left rounded-lg px-1.5 py-1 text-[11.5px] ${
                      unreachable
                        ? `${ATTENTION_SURFACE} ${ATTENTION_OUTLINE}`
                        : 'hover:bg-cream'
                    }`}
                  >
                    <span className="flex items-center gap-1.5">
                      <span
                        className={`w-3.5 text-right tabular-nums ${
                          unreachable ? ATTENTION_INK : 'text-inkMuted'
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span
                        className={`flex-1 truncate font-semibold ${
                          unreachable ? ATTENTION_INK : ''
                        }`}
                      >
                        {unit.title || '(untitled)'}
                      </span>
                      <StatusPill
                        active={unit.active}
                        small
                        testId={`map-lesson-status-${trackId}-${lessonId}`}
                      />
                      {/* D6/D7 — a shared Lesson really does count for several
                          Tracks, and its position is per-Track. The marker gives
                          the TRUE count so three chips do not read as three
                          different Lessons. */}
                      {shared > 1 && (
                        <span
                          data-testid={`map-lesson-shared-${trackId}-${lessonId}`}
                          className="text-[9.5px] font-bold text-inkMuted tabular-nums shrink-0"
                          title={MAP_COPY.sharedLessonTitle(shared)}
                        >
                          ●{shared}
                        </span>
                      )}
                    </span>
                    {/* H1 is a TWO-LINE block: a badge on the title row steals
                        the width the title needs and truncates the one thing
                        that identifies the Lesson. */}
                    {unreachable && (
                      <span
                        data-testid={`map-h1-${lessonId}`}
                        className={`flex items-start gap-1 pl-5 text-[10.5px] font-bold ${ATTENTION_INK}`}
                      >
                        <span aria-hidden className="shrink-0">
                          {HAZARD_GLYPH.h1}
                        </span>
                        <span>{MAP_COPY.unreachableLesson()}</span>
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ol>
        )}

        {!t.active && (
          <p className="px-2.5 pb-2 text-[10.5px] text-inkMuted">{MAP_COPY.hiddenTrackNote()}</p>
        )}
      </article>
    );
  };

  return (
    <div
      data-testid="curriculum-map"
      className="flex-1 min-h-0 overflow-y-auto overflow-x-auto p-5"
      style={{ minHeight: 480 }}
    >
      {/* `data-map-host` marks the positioned wrapper the edge layer measures
          against. It is also the handle AC6's instrument needs to supply a
          geometry: jsdom returns an all-zero rect for everything, and a
          containment assertion against 0x0 boxes passes vacuously. */}
      <div ref={setHost} data-map-host className="relative">
        <MapEdgeLayer edges={map.edges} host={host} revision={map} />
        <div className="relative flex gap-12 items-start" data-tier-row>
          {map.tiers.map((ids, tier) => (
            <div
              key={tier}
              data-tier={tier}
              data-testid={`map-tier-${tier}`}
              className="flex flex-col gap-4"
              // ─── TIER COLUMN WIDTH — read the measurement, not the mockup ───
              //
              // These values are the mockup's (S7 note 8) and they are correct.
              // The SENTENCE that used to sit here was not, and it is replaced
              // rather than softened: it said the fifth column "earns" a
              // scrollbar, which implies the overflow is a function of window
              // width and that widening the window clears it. It is not, and it
              // does not.
              //
              // Measured against the SHIPPED geometry in a browser (design's
              // harness, commit 6524211):
              //
              //   * four columns fit from 1142px up;
              //   * a FIFTH NEVER FITS AT ANY VIEWPORT. The page shell caps at
              //     max-w-[1320px] and this canvas saturates at 1270px, so the
              //     overflow is byte-identical at 1440, 1920, 2560 and 3840;
              //   * the scrollbar is therefore a function of TIER COUNT, not of
              //     window size. A user who widens the window gets nothing.
              //
              // WHY THIS MATTERS FOR THE LAUNCH TENANT: Grove is at four tiers
              // today, with zero headroom. One authored cycle lifts its members
              // into the loop group, which renders as a FIFTH column — and puts
              // Grove into permanent horizontal scroll.
              //
              // DO NOT go chasing a smaller `minWidth` to buy a fifth column.
              // Every fitting candidate lands on exactly 1270 with zero slack,
              // and six columns scroll under all of them; the constraint is the
              // shell, not this number. Making the overflow discoverable is a
              // real UI change, needs a design pass, and is deferred.
              style={{ minWidth: 232, maxWidth: 268, flex: 1 }}
            >
              <div className="pb-1">
                <div className="text-[12px] font-extrabold text-ink">
                  {MAP_COPY.tierHeading(tier)}
                </div>
                <div className="text-[11px] text-inkMuted">{MAP_COPY.tierSubtitle(tier)}</div>
              </div>
              {ids.map(renderTrackNode)}
            </div>
          ))}

          {/* H4's groups sit AFTER the last tier column and carry no `data-tier`
              — the edge layer reads that absence as "no tier" and routes their
              connectors through the gutter rather than as clean forward arrows.
              Lifting the whole component out is what keeps the tier walk from
              ever entering a cycle, which is how AC12 terminates. */}
          {map.loopGroups.map((group, i) => (
            <div
              key={`loop-${i}`}
              data-testid={`map-loop-group-${i}`}
              className="flex flex-col gap-4"
              style={{ minWidth: 232, maxWidth: 268, flex: 1 }}
            >
              <div className="pb-1">
                <div className={`text-[12px] font-extrabold ${ATTENTION_INK}`}>
                  {MAP_COPY.loopGroupNoTier()}
                </div>
                <div className="text-[11px] text-inkMuted">{MAP_COPY.loopGroupLabel()}</div>
              </div>
              <div className={`rounded-2xl border ${ATTENTION_BORDER} ${ATTENTION_SURFACE} p-2 flex flex-col gap-3`}>
                {group.map(renderTrackNode)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ===== In no {Track} ===== */}
      {/* Q3 — the visible heading matches the shipped filter chip and count line
          one pane to the right. "Unfiled" survives only as the `data-testid`, so
          the spec's word still addresses the thing in tests. A second
          user-visible noun for one population is the drift the terminology rule
          exists to prevent. */}
      <section className="mt-8 pt-4 border-t border-softBorder" data-testid="map-unfiled-region">
        <div className="flex items-baseline gap-2 mb-2">
          <h4 className="font-bold text-[12.5px]">{MAP_COPY.unfiledHeading()}</h4>
          <span className="text-[11px] text-inkMuted">{MAP_COPY.unfiledCount(unfiled.length)}</span>
        </div>
        {unfiled.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {unfiled.map((unit) => {
              const unreachable = map.unreachableLessonIds.has(unit.id);
              return (
                <button
                  key={unit.id}
                  type="button"
                  data-testid={`map-unfiled-lesson-${unit.id}`}
                  onClick={() => onOpenLesson?.(unit.id)}
                  title={MAP_COPY.lessonChipTitle(unit.title || '(untitled)')}
                  className={`inline-flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-[11.5px] ${
                    unreachable
                      ? `${ATTENTION_SURFACE} ${ATTENTION_OUTLINE}`
                      : 'bg-white border border-softBorder hover:bg-cream'
                  }`}
                >
                  <span className={`font-semibold ${unreachable ? ATTENTION_INK : ''}`}>
                    {unit.title || '(untitled)'}
                  </span>
                  <StatusPill active={unit.active} small />
                  {unreachable && (
                    <span
                      data-testid={`map-h1-${unit.id}`}
                      className={`text-[10px] font-bold ${ATTENTION_INK} whitespace-nowrap`}
                    >
                      <span aria-hidden>{HAZARD_GLYPH.h1} </span>
                      {MAP_COPY.unreachableLesson()}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
        <p className="text-[11px] text-inkMuted mt-2 max-w-[70ch]">{MAP_COPY.unfiledBlurb()}</p>
      </section>

      {/* ===== The hazard rail ===== */}
      {/* Q7 — affirmative, modelled on the shipped "In no Track" empty state,
          because a negation "would read as a failed query". This is the closest
          string on the canvas to the D11 boundary: it claims reachability, and
          reachability is one clause from a claim about a learner. It survives
          because it holds for every learner at once and names no status. Do not
          paraphrase it toward status vocabulary. */}
      {hazardFree && (
        <section
          data-testid="map-no-hazards"
          className="mt-6 rounded-xl border border-softBorder bg-cream/60 px-3.5 py-2.5"
        >
          <p className="text-[12.5px] font-bold text-ink">{MAP_COPY.noHazardsHeadline()}</p>
          <p className="text-[11.5px] text-inkMuted mt-0.5">{MAP_COPY.noHazardsBody()}</p>
        </section>
      )}

      {/* D3 stated where the tenant it applies to will read it — Ladle & Leaf
          sees no mark because of their SHAPE, not because nothing was checked. */}
      {activeCount <= 1 && tracks.length > 0 && (
        <p data-testid="map-single-track-note" className="mt-3 text-[11px] text-inkMuted max-w-[70ch]">
          {MAP_COPY.singleTrackNote()}
        </p>
      )}
    </div>
  );
};

export default CurriculumMapView;
